import type { StatusTone } from '@/shared/ui/status-badge'

interface StatusMeta {
  label: string
  tone: StatusTone
}

// Механические исходы звонка — ровно словарь `callv1.Outcome`. Своих значений UI не
// изобретает: разошедшийся словарь красит живой исход как незнакомый.
const outcomes = {
  answered: { label: 'Отвечен', tone: 'ok' },
  busy: { label: 'Занято', tone: 'warn' },
  no_answer: { label: 'Нет ответа', tone: 'warn' },
  rejected: { label: 'Отклонён', tone: 'warn' },
  failed: { label: 'Сбой', tone: 'danger' },
} satisfies Record<string, StatusMeta>

export type CallOutcome = keyof typeof outcomes

export const callOutcomes = Object.keys(outcomes) as CallOutcome[]

export function callOutcome(outcome: string): StatusMeta {
  // Незнакомое значение отдаём как есть: отчёт может приехать от движка новее UI.
  return outcomes[outcome as CallOutcome] ?? { label: outcome || '—', tone: 'muted' }
}

export const liveKindLabels: Record<string, string> = {
  started: 'Идёт разговор',
  ringing: 'Дозвон',
  connected: 'Соединён',
}

export function liveKindLabel(kind: string): string {
  return liveKindLabels[kind] ?? kind
}

// Словари механики callv1: сырые значения контракта — оператору словами.
// Незнакомое значение отдаём как есть: отчёт может приехать от движка новее UI.
function dict(labels: Record<string, string>) {
  return (value: string): string => labels[value] ?? value
}

export const endedReasonLabel = dict({
  hangup_remote: 'Положил абонент',
  hangup_bot: 'Положил бот',
  hangup_operator: 'Оборвал оператор',
  error: 'Ошибка',
  deadline: 'Лимит времени',
})

export const lineLabel = dict({
  sip: 'SIP-транк',
  webrtc: 'Браузер (WebRTC)',
})

export const languageLabel = dict({
  ru: 'русский',
  en: 'английский',
  ar: 'арабский',
})

export const intentHintLabel = dict({
  said_goodbye: 'Бот попрощался',
  goal_met: 'Цель достигнута',
  user_goodbye: 'Абонент попрощался',
  not_interested: 'Не интересно',
  rude_user: 'Грубость абонента',
  wrong_number: 'Не тот номер',
})

// Чем сработал коммит конца реплики абонента: быстрый путь против бэкстопа.
export const settleReasonLabel = dict({
  none: 'нет данных',
  nogate: 'гейт выключен',
  final: 'финальный сегмент',
  stable: 'гипотеза осела',
  hard: 'бэкстоп по тишине',
})

export const finishReasonLabel = dict({
  stop: 'договорил',
  length: 'упёрся в лимит',
  error: 'ошибка',
  interrupted: 'оборван',
})

export const decideActionLabel = dict({
  speak: 'продолжать',
  end_call: 'завершить звонок',
})

export const specKindLabel = dict({
  none: 'не было',
  discarded: 'сожжена впустую',
  promoted: 'пригодилась',
})

// Вид хода задаёт якорь его таймлинии: у приветствия и служебных ходов речи
// абонента нет, поэтому их латентность в общие медианы не входит.
export const turnKindLabel = dict({
  greeting: 'приветствие',
  normal: 'ход',
  resume: 'возобновление',
  nudge: 'переспрос',
  end_call: 'прощание',
})

export const interruptReasonLabel = dict({
  barge_live: 'перебил абонент',
  hangover: 'заглушен хвост',
  preempt: 'вытеснен коротким ответом',
  evicted: 'вытеснен следующим ходом',
  teardown: 'звонок завершился',
  deadline: 'лимит времени',
})

// Судьба реплики абонента — ответ на «почему бот промолчал».
export const utteranceOutcomeLabel = dict({
  turn: 'породила ход',
  glued: 'склеена со следующей',
  empty: 'без текста',
  backchannel: 'бэкченнел, бот продолжил',
  withdrawn: 'ход снят — абонент продолжил',
  swallowed: 'проглочена окном приветствия',
  unfinished: 'не завершена',
})

export function utteranceTone(outcome: string): StatusTone {
  if (outcome === 'turn' || outcome === 'glued') return 'ok'
  if (outcome === 'backchannel') return 'accent'
  if (outcome === 'swallowed' || outcome === 'withdrawn') return 'warn'
  if (outcome === 'empty') return 'muted'
  return 'muted'
}

export const emptyReactionLabel = dict({
  ignored: 'проигнорирован',
  resume: 'бот договорил',
  nudge: 'бот переспросил',
})

export const nudgeKindLabel = dict({
  unheard: 'переспрос нераспознанной речи',
})

export const ctrlKindLabel = dict({
  reconnect: 'управляющий канал переподключён',
  resync_synth_closed: 'закрытие звонка восстановлено по resync',
  hangup_retry: 'hangup повторён через разрыв',
})

export const cascadeStageLabel = dict({
  speculate: 'спекулятивно',
  commit: 'перед коммитом',
})

export const bargeKindLabel = dict({
  live: 'оборвало реплику',
  hangover: 'глушило хвост',
  greet_suppressed: 'подавлено окном приветствия',
})

export const bargeOutcomeLabel = dict({
  none: 'без последствий',
  commit: 'абонент правда заговорил',
  resume: 'шум, реплика возобновлена',
  watchdog_resume: 'шум, речь вернул сторож',
})

// Перебивание считается ложным, когда после него реплику пришлось возобновлять.
export function bargeTone(outcome: string): StatusTone {
  if (outcome === 'commit') return 'accent'
  if (outcome === 'resume' || outcome === 'watchdog_resume') return 'warn'
  return 'muted'
}
